import React, { Fragment, memo } from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { formatPrice, getSize, getTotal, QTY_BIG_SELECT } from '@aytm/app/swidget/questions/components/sh/functions'

import cartStyle from '@aytm/app/swidget/questions/styles/sh/cart.module.scss'
import Select from '@aytm/lib/rfd-ui/components/basic/select'
import EmptyImage from '@aytm/app/swidget/questions/images/sh/empty.svg'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects'

const onFocus = () => {
  hapticEffects.soft()
}

Cart.propTypes = {
  questionSchema: PropTypes.instanceOf(Map).isRequired,
  answer: PropTypes.instanceOf(Map).isRequired,
  fullHeight: PropTypes.bool,
  fullScreen: PropTypes.bool,
  onProductClick: PropTypes.func,
  onQtyChange: PropTypes.func,
  onDelete: PropTypes.func,
  locale: PropTypes.instanceOf(Map).isRequired,
  mobile: PropTypes.bool
}

export function Cart(props) {
  const { questionSchema, answer, fullHeight, onProductClick, onQtyChange, onDelete, locale, fullScreen, mobile } =
    props
  const answers = answer.get('answers')
  const maxQty = questionSchema.get('max_qty_per_product')
  const maxCartSize = questionSchema.get('max_cart_size')
  const maxMoney = questionSchema.get('max_money_spent')

  const onSelectQty = (val, item) => {
    hapticEffects.selection()
    return onQtyChange(val, item)
  }

  return (
    <div
      className={
        `${cartStyle.cart} ` +
        (fullHeight ? `${cartStyle.fullHeight}` : ``) +
        (fullScreen ? `${cartStyle.fullScreen}` : ``)
      }
      data-testid="SW-SH-CartScreen"
    >
      {answers.filter(answer => answer.get('count') > 0).size ? (
        answers
          .filter(answer => answer.get('count') > 0)
          .toArray()
          .map((good, i) => {
            const item = good.get('item')
            const itemPrice = item.get('discounted_price') || item.get('price')
            const total = getTotal(answer, item)
            const size = getSize(answer, item)

            return (
              <Fragment key={'good' + i}>
                <div className={cartStyle.good} data-testid="SW-SH-CartProduct">
                  <div className={cartStyle.photo}>
                    <img
                      data-testid="SW-SH-CartProduct-Image"
                      src={
                        item.get('currentPicture')
                          ? item.getIn(['currentPicture', 'trimmed_path'])
                          : item.getIn(['image', 'trimmed_path'])
                      }
                      alt={item.getIn(['image', 'alt_text']) || locale.get('noAlt')}
                      className={cartStyle.img}
                      onClick={() => onProductClick(item)}
                    />
                  </div>
                  <div className={cartStyle.description} data-testid="SW-SH-CartProduct-Description">
                    <div className={cartStyle.flexContainer}>
                      <div className={cartStyle.spans}>
                        <span
                          data-testid="SW-SH-CartProduct-Header"
                          className={cartStyle.header}
                          dangerouslySetInnerHTML={{ __html: item.get('text') }}
                        />
                      </div>
                    </div>
                    <div className={cartStyle.text} dangerouslySetInnerHTML={{ __html: item.get('description') }} />
                    <div className={cartStyle.buttons} data-testid="SW-SH-CartProduct-Buttons">
                      <Select
                        className={cartStyle.combo}
                        smallHeight
                        newIcon={true}
                        options={QTY_BIG_SELECT.filter(
                          v =>
                            v.get('value') == good.get('count') ||
                            ((!maxQty || v.get('value') <= maxQty) &&
                              (!maxCartSize || v.get('value') + size <= maxCartSize) &&
                              (!maxMoney || v.get('value') * itemPrice + total <= maxMoney))
                        )}
                        width={80}
                        onSelect={val => onSelectQty(val, item)}
                        showSelected={true}
                        onFocus={onFocus}
                        value={good.get('count')}
                      />
                      <div
                        data-testid="SW-SH-CartProduct-Button-Delete"
                        onClick={() => onDelete(item)}
                        className={cartStyle.button}
                      >
                        {locale.getIn(['sh', 'deleteFromCart'])}
                      </div>
                    </div>
                  </div>
                  {!questionSchema.get('hide_price') && (
                    <div className={cartStyle.price} data-testid="SW-SH-CartProduct-Price">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: formatPrice(
                            good.get('count') * (item.get('discounted_price') || item.get('price')),
                            questionSchema.get('price_mask')
                          )
                        }}
                      />
                      {item.get('discounted_price') ? (
                        <p
                          className={cartStyle.smallCrossed}
                          dangerouslySetInnerHTML={{
                            __html:
                              formatPrice(item.get('price'), questionSchema.get('price_mask')) +
                              (good.get('count') > 1 ? ' ' + locale.getIn(['sh', 'each']) : '')
                          }}
                        />
                      ) : null}
                      {good.get('count') > 1 && (
                        <p
                          className={cartStyle.small}
                          dangerouslySetInnerHTML={{
                            __html:
                              formatPrice(
                                item.get('discounted_price') || item.get('price'),
                                questionSchema.get('price_mask')
                              ) +
                              ' ' +
                              locale.getIn(['sh', 'each'])
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </Fragment>
            )
          })
      ) : (
        <div className={`${cartStyle.empty} ${mobile ? cartStyle.emptyMobile : ''}`} data-testid="SW-SH-CartEmpty">
          <img src={EmptyImage} alt="" />
          <div>{locale.getIn(['sh', 'emptyCard'])}</div>
        </div>
      )}
    </div>
  )
}

export default memo(
  Cart,
  (prevProps, nextProps) =>
    prevProps.questionSchema === nextProps.questionSchema &&
    prevProps.answer === nextProps.answer &&
    prevProps.fullHeight === nextProps.full &&
    prevProps.onProductClick === nextProps.onProcessed &&
    prevProps.onQtyChange === nextProps.onQtyChange &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.locale === nextProps.locale
)
